import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Small copy-to-clipboard button with a transient "Copied" state. */
export function CopyButton({
  value,
  label = 'Copy',
  ariaLabel,
  className,
}: {
  value: string;
  label?: string;
  /** Accessible name while idle, for when several buttons share one visible label (a list of "Copy"). */
  ariaLabel?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — no-op; the value
      // is still visible for manual selection.
    }
  };

  return (
    <button
      type="button"
      onClick={onCopy}
      // While "Copied" is showing, the visible text is the accessible name, so the confirmation is announced.
      aria-label={copied ? undefined : ariaLabel}
      className={cn(
        'inline-flex items-center gap-1.5 rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-navy',
        className,
      )}
    >
      {copied ? <Check className="h-4 w-4 text-green" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
      {copied ? 'Copied' : label}
    </button>
  );
}
